import React, { useState, useEffect, useMemo } from "react";
import { 
	Dialog,
	DialogContent,
	Box, 
	Stack,
	IconButton,
	Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IconX } from "@tabler/icons-react";
import { getCollection } from "../../utils/pureDataFetch";
import CollectionTitleSection from "../../components/CollectionTitleSection";
import CollectionThreadContent from "../../components/CollectionThreadContent";
import { CollectionKeyMetricSummarySection } from "../../template/metric/CollectionKeyMetricSummarySection";
import { processThreadMediaWithProxy } from "../../utils/mediaDownloader";
import { useUser } from "../../contexts/UserContext";
import colorsCustom from "../../designTokens/colorsCustom";

/**
 * CollectionPopupModal
 * 매거진 페이지에서 컬렉션을 팝업으로 표시하는 모달
 * CollectionPage 레이아웃 기반에서 목차 제거
 */
const CollectionPopupModal = ({ 
	open, 
	onClose, 
	collectionId, 
	isMobile 
}) => {
	const theme = useTheme();
	const { isLogin } = useUser();
	
	const [collection, setCollection] = useState(null);
	const [selectedThread, setSelectedThread] = useState(null);
	const [metrics, setMetrics] = useState({
		views: 0,
		likes: 0,
		replies: 0,
		reposts: 0,
	});

	// 미디어 정보를 처리하는 함수
	const processThreadWithMedia = (thread) => {
		let childrenArray = [];
		if (
			thread.children &&
			thread.children.data &&
			Array.isArray(thread.children.data)
		) {
			childrenArray = thread.children.data;
		}

		const hasMedia = !!(thread.media_url || childrenArray.length > 0);
		const mediaUrls = [];

		if (childrenArray.length > 0) {
			childrenArray.forEach((child, index) => {
				if (child.media_url) {
					mediaUrls.push({
						url: child.media_url,
						type: child.media_type,
						alt: child.alt_text || "",
					});
				}
			});
		} else if (thread.media_url) {
			mediaUrls.push({
				url: thread.media_url,
				type: thread.media_type,
				alt: thread.alt_text || "",
			});
		}

		const processedThread = {
			...thread,
			hasMedia,
			mediaType: thread.media_type,
			mediaUrls,
			isCarousel: !!(childrenArray.length > 1),
		};

		return processedThread;
	};

	useEffect(() => {
		if (!open || !collectionId) return;

		getCollection(collectionId).then(async (collection) => {
			// 컬렉션의 각 스레드에 미디어 정보 추가 및 날짜순 정렬
			const threadsWithMedia = collection.threadList
				.map(processThreadWithMedia)
				.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

			// 미디어 처리
			const threadsWithDownloadedMedia = threadsWithMedia.map((thread) => {
				if (thread.hasMedia) {
					try {
						return processThreadMediaWithProxy(thread);
					} catch (error) {
						console.error(
							`❌ Failed to process proxy for thread ${thread.id}:`,
							error
						);
						return thread;
					}
				}
				return thread;
			});

			// 이미지 preload
			const preloadPromises = threadsWithDownloadedMedia
				.filter((thread) => thread.hasMedia && thread.mediaUrls)
				.flatMap((thread) =>
					thread.mediaUrls
						.filter((media) => media.type !== "VIDEO")
						.map((media) => {
							return new Promise((resolve) => {
								const img = new Image();
								img.onload = () => resolve();
								img.onerror = () => resolve();
								img.src = media.url;
							});
						})
				);

			Promise.all(preloadPromises).then(() => {
				console.log(
					`🎉 All images preloaded: ${preloadPromises.length} images`
				);
			});

			const processedCollection = {
				...collection,
				threadList: threadsWithDownloadedMedia,
			};

			setCollection(processedCollection);
			setSelectedThread(processedCollection.threadList[0]);

			// 메트릭 계산
			const totalMetrics = processedCollection.threadList.reduce(
				(acc, thread) => {
					acc.views += thread.views || 0;
					acc.likes += thread.likes || 0;
					acc.replies += thread.replies || 0;
					acc.reposts += thread.reposts || 0;
					return acc;
				},
				{ views: 0, likes: 0, replies: 0, reposts: 0 }
			);

			setMetrics(totalMetrics);
		});
	}, [open, collectionId]);

	// 로그인하지 않은 경우 미디어 표시를 숨기기 위해 선택된 스레드의 미디어 정보를 제거
	const selectedThreadForView = useMemo(() => {
		if (!selectedThread) return null;
		if (isLogin) return selectedThread;
		return {
			...selectedThread,
			hasMedia: false,
			mediaUrls: [],
		};
	}, [selectedThread, isLogin]);

	// 컬렉션이 비어있는지 확인
	const isEmptyCollection =
		collection &&
		(!collection.threadList || collection.threadList.length === 0);

	const handleClose = () => {
		setCollection(null);
		setSelectedThread(null);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="lg"
			fullWidth
			fullScreen={isMobile}
			sx={{
				"& .MuiDialog-paper": {
					height: isMobile ? "100%" : "90vh",
					maxHeight: isMobile ? "100%" : "90vh",
					borderRadius: isMobile ? 0 : 2,
				},
			}}
		>
			<DialogContent sx={{ p: 0, height: "100%", overflow: "hidden" }}>
				<Box
					sx={{
						height: "100%",
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					}}
				>
					{/* 헤더 - 닫기 버튼 */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "flex-end",
							alignItems: "center",
							p: 2,
							borderBottom: `1px solid ${theme.palette.divider}`,
							flexShrink: 0,
						}}
					>
						<IconButton onClick={handleClose} size="small">
							<IconX size={24} />
						</IconButton>
					</Box>

					{/* 스크롤 가능한 콘텐츠 영역 */}
					<Box
						sx={{
							flex: 1,
							overflow: "auto",
							px: isMobile ? 2 : 4,
							py: 2,
						}}
					>
						{collection ? (
							<Stack
								spacing={isMobile ? 4 : 6}
								sx={{ width: "100%" }}
							>
								{/* 메트릭 섹션 */}
								{!isEmptyCollection && (
									<Box sx={{ width: "100%" }}>
										<CollectionKeyMetricSummarySection
											isXL={isMobile}
											isSM={isMobile}
											metrics={metrics}
											views={metrics.views}
											likes={metrics.likes}
											replies={metrics.replies}
											reposts={metrics.reposts}
											numOfThreads={collection.threadList.length}
										/>
									</Box>
								)}

								{/* 타이틀 섹션 */}
								<CollectionTitleSection
									isMobile={isMobile}
									collection={collection}
									setModalOpen={() => {}}
									handleDeleteCollection={() => {}}
									isMe={false}
									hideActions={true}
								/>

								{/* 구분선 */}
								<Box
									sx={{
										width: "100%",
										height: "1px",
										backgroundColor: colorsCustom.ui.dark.subtle,
									}}
								/>

								{/* 컬렉션이 비어있지 않은 경우에만 콘텐츠 표시 */}
								{!isEmptyCollection ? (
									selectedThreadForView && (
										<Box sx={{ width: "100%" }}>
											<CollectionThreadContent
												selectedThread={selectedThreadForView}
												setSelectedThread={setSelectedThread}
												threadList={collection.threadList}
												isMobile={isMobile}
												collectionID={collectionId}
												isVisible={true}
												setIsVisible={() => {}}
												collection={collection}
												isMe={false}
												hideTOC={true}
										isLogin={isLogin}
											/>
										</Box>
									)
								) : (
									/* 빈 컬렉션 안내 메시지 */
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											minHeight: "40vh",
											textAlign: "center",
											gap: 2,
										}}
									>
										<Typography
											variant="h5"
											sx={{
												fontWeight: 600,
												color: "text.primary",
												mb: 2,
											}}
										>
											빈 컬렉션입니다
										</Typography>
										<Typography
											variant="body1"
											sx={{
												color: "text.secondary",
											}}
										>
											이 컬렉션에는 아직 스레드가 없습니다.
										</Typography>
									</Box>
								)}
							</Stack>
						) : (
							/* 로딩 상태 */
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									minHeight: "40vh",
								}}
							>
								<Typography variant="body2" color="text.secondary">
									컬렉션을 불러오는 중...
								</Typography>
							</Box>
						)}
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default CollectionPopupModal;